import "./style.scss";
import { Form } from "react-bootstrap";
import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Scale from "../Scale";
import AuditMode from "../AuditMode";
import NumberInputGroup from "../NumberInputGroup";
import Robo from "../../icons/robo.svg?react";
import { FaCalendarAlt } from "react-icons/fa";
import DateRangePicker from "../DateRangePicker";
import { getDateFormat, getTodayISODate } from "../../utils";
import Clock from "../../icons/Clock.svg?react";
import Mode from "../../icons/AuditMode.svg?react";
import Skill from "../../icons/skill-level.svg?react";
import Frequency from "../../icons/Frequencymail.svg?react";
import Step4Alert from "../../icons/Step4Alert.svg?react";
import KickOff from "../../icons/KickOff.svg?react";
import AuditSequencing, { type Policy } from "../AuditSquence";
import { setSequenceSteps } from "../../reducers/Audit";
import type { RootState } from "../../store";
import PolicyIcon from "../../icons/Policy.svg?react";
import TechStackIcon from "../../icons/TechStack.svg?react";
import InventoryIcon from "../../icons/Inventory.svg?react";
import ArtifactIcon from "../../icons/Artifact.svg?react";

interface TeamMember {
  id: string;
  name: string;
  email: string;
}
interface AuditeMember {
  email: string;
  name: string;
  memberId: string;
  role: string;
}
interface Teamlead {
  email: string;
  name: string;
  memberId: string;
  role: string;
}

export interface AuditData {
  teamMembers: TeamMember[];
  auditLevel: number;
  auditType: number;
  auditSla: number;
  escalationDay: number;
  kickOffMeeting: boolean;
  auditeeInfo: {
    members: TeamMember[];
    applicationID: number[];
    startDate: Date;
    startDateMarker: Date;
    endDate: Date;
    endDateMarker: Date;
    auditee: string;
    sequencedata?: any[];
    emails: AuditeMember[];
    organizations: AuditeMember[];
    auditThemes: AuditeMember[];
    auditScope: string;
    applicationDesc: string;
    auditeeTeamLead: Teamlead[];
    isTeamLead?: boolean;
  };
}

interface Props {
  auditData: AuditData;
  validated: boolean;
  setAuditData: React.Dispatch<React.SetStateAction<AuditData>>;
  aiSuggestion?: any;
  sequence?: any;
  auditUpdatedata: any;
  showAISuggestion: boolean;
  AIloading: boolean;
  showAICompanion: boolean;
  isInValidDuration: boolean;
  checked: boolean;
  handleTimeoutcall: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleRemainderCall: (e: React.FocusEvent<HTMLInputElement>) => void;
  onDateChange: any;
  updateAuditeeInfo: any;
  setShowAISuggestion: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAICompanion?: React.Dispatch<React.SetStateAction<boolean>>;
  handleToggle?: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleDatechangeAPICall?: (e: React.FocusEvent<HTMLInputElement>) => void;
  sequenceMode?: "default" | "custom";
  setSequenceMode?: React.Dispatch<React.SetStateAction<"default" | "custom">>;
}

const Step4AuditPreference: React.FC<Props> = ({
  validated,
  auditData,
  setAuditData,
  setShowAISuggestion,
  showAISuggestion,
  aiSuggestion,
  handleToggle,
  checked,
  AIloading,
  auditUpdatedata,
  isInValidDuration,
  setShowAICompanion,
  showAICompanion,
  handleTimeoutcall,
  handleRemainderCall,
  onDateChange,
  handleDatechangeAPICall,
  updateAuditeeInfo,
  sequence,
  sequenceMode,
  setSequenceMode,
}) => {
  const dispatch = useDispatch();
  const sequenceSteps = useSelector(
    (state: RootState) => state.auditee.step2.sequenceSteps,
  );

  const startDate = auditData?.auditeeInfo?.startDate;
  const endDate = auditData?.auditeeInfo?.endDate;

  let durationDays = 0;

  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      durationDays = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
      );
    }
  }

  const lastPayloadRef = useRef<string>("");

  const handleSequenceUpdate = useCallback(
    (steps: any[]) => {
      const payload = steps.flatMap((step, stepIndex) => {
        const sequenceOrder = stepIndex + 1;
        // For the first sequence (sequenceOrder === 1), always set questionValidationRequired to true
        const isFirstSequence = sequenceOrder === 1;
        return step.policies.map((policy: any) => ({
          sequenceId: parseInt(policy.id),
          sequenceOrder: sequenceOrder,
          questionValidationRequired: isFirstSequence
            ? true
            : step.preSendReview || false,
          responseValidationRequired: step.responseCheck || false,
        }));
      });

      const payloadKey = JSON.stringify(payload);
      if (payloadKey !== lastPayloadRef.current) {
        lastPayloadRef.current = payloadKey;
        dispatch(setSequenceSteps(payload));
        updateAuditeeInfo("sequencedata", payload);
      }
    },
    [updateAuditeeInfo, dispatch],
  );
  const transformedSequences: Policy[] = (() => {
    const sourceSequences = sequence || [];

    return sourceSequences.map((seq: any, index: number) => {
      // Define icon colors in a cycle
      const iconColors: Array<"orange" | "blue" | "teal" | "purple"> = [
        "orange",
        "blue",
        "teal",
        "purple",
      ];

      // Define SVG icon identifiers for sequences
      const icons = ["Policy", "TechStack", "Inventory", "Artifact"];

      return {
        id: seq.sequenceId?.toString() || `seq-${index}`,
        name: seq.sequenceName || seq.name || `Sequence ${index + 1}`,
        description: seq.description || "No description available",
        icon: icons[index % icons.length],
        iconColor: iconColors[index % iconColors.length],
      };
    });
  })();

  const reconstructInitialSteps = useCallback(() => {
    const sourceData =
      sequenceSteps && sequenceSteps.length > 0
        ? sequenceSteps
        : auditUpdatedata?.auditSequences;

    if (
      !sourceData ||
      sourceData.length === 0 ||
      !transformedSequences.length
    ) {
      return undefined;
    }

    // Group sequences by sequenceOrder
    const stepGroups: { [key: number]: any[] } = {};
    sourceData.forEach((seq: any) => {
      const order = seq.sequenceOrder;

      if (order === undefined || order === null) {
        return;
      }

      if (!stepGroups[order]) {
        stepGroups[order] = [];
      }
      stepGroups[order].push(seq);
    });
    const initialSteps = Object.keys(stepGroups)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map((order, index) => {
        const seqGroup = stepGroups[parseInt(order)];

        // Find policies from transformed sequences
        const policies = seqGroup
          .map((seq: any) => {
            return transformedSequences.find(
              (p) => p.id === seq.sequenceId.toString(),
            );
          })
          .filter((p: any): p is Policy => p !== undefined);

        return {
          id: `step-${Date.now()}-${index}`,
          policies: policies,
          responseCheck: seqGroup[0]?.responseValidationRequired || false,
          preSendReview: seqGroup[0]?.questionValidationRequired || false,
        };
      });
    return initialSteps;
  }, [sequenceSteps, transformedSequences, auditUpdatedata?.auditSequences]);

  return (
    <div className="content-card ">
      <div className="step-content">
        <div className="step-header">
          <h2>Audit Preference</h2>
          <div className="align-AI">
            {!showAICompanion && (
              <div
                role="button"
                tabIndex={0}
                className="outer-circle outer-circle-margin"
                data-testid="robo-icon"

                onClick={() => setShowAICompanion?.(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowAICompanion?.(true);
                  }
                }}
              >
                <Robo className="ai-bot-button" />
              </div>
            )}
          </div>
        </div>

        <div className="form-container">
          <div className="form-group">
            <label className="gap-0">
              <Skill className="me-2" />
              Audit Level <span className="text-danger">*</span>
            </label>
            <div
              className={
                validated && auditData.auditLevel === 0 ? "is-invalid" : ""
              }
              data-testid="level-3"
            >
              <Scale
                value={auditData.auditLevel}
                onChange={(value) => {
                  setAuditData((prev) => ({
                    ...prev,
                    auditLevel: value,
                  }));
                }}
              />
            </div>
            <Form.Control.Feedback type="invalid" className="text-start">
              {validated && auditData.auditLevel === 0
                ? "Please fill this field."
                : ""}
            </Form.Control.Feedback>
          </div>
          <div className="form-row-3 mt-4">
            <div className="form-group step4background">
              <label className="gap-0">
                <FaCalendarAlt className="me-2" /> Duration{" "}
                <span className="text-danger">*</span>
              </label>
              <div
                className={
                  validated && isInValidDuration
                    ? "duration-inputs valid-error"
                    : "duration-inputs"
                }
                data-testid="Duration"
              >
                <DateRangePicker
                  onDateChange={onDateChange}
                  handleDatechangeAPICall={handleDatechangeAPICall}
                  FromDate={
                    isInValidDuration && !auditUpdatedata?.startDate
                      ? "dd-mm-yyyy"
                      : getDateFormat(
                        auditData.auditeeInfo.startDate,
                        "DD-MM-YYYY",
                      )
                  }
                  ToDate={
                    isInValidDuration && !auditUpdatedata?.endDate
                      ? "dd-mm-yyyy"
                      : getDateFormat(
                        auditData.auditeeInfo.endDate,
                        "DD-MM-YYYY",
                      )
                  }
                  min={getTodayISODate()}
                />
              </div>
              <Form.Control.Feedback type="invalid" className="text-start">
                {validated &&
                  isInValidDuration &&
                  !auditUpdatedata?.endDate &&
                  "Please fill this field."}
              </Form.Control.Feedback>
            </div>
            <div className="form-group step4align">
              <div>
                <label className="gap-0">
                  <Clock className="me-2" />
                  Response Time <span className="text-danger">*</span>
                </label>

                <div
                  className={
                    (validated && auditData.auditSla === 0) ||
                      auditData.auditSla >= durationDays
                      ? "is-invalid borderslaBox sla-input"
                      : "sla-input"
                  }
                >
                  <NumberInputGroup
                    value={auditData.auditSla}
                    name="slatime"
                    onChange={(value: number) =>
                      setAuditData((prev) => ({
                        ...prev,
                        auditSla: value,
                      }))
                    }
                    onBlur={handleTimeoutcall}
                    text="Days"
                  />
                </div>
                <Form.Control.Feedback type="invalid" className="text-start">
                  {validated && auditData.auditSla === 0
                    ? "Please fill this field."
                    : auditData.auditSla > 0 &&
                      auditData.auditSla >= durationDays
                      ? "Response Time should be less than Duration."
                      : ""}
                </Form.Control.Feedback>
              </div>

              <div>
                <label className="gap-0">
                  <Step4Alert className="Frequencyalign" />
                  Reminder Alert<span className="text-danger">*</span>
                </label>

                <div className={"sla-input"}>
                  <NumberInputGroup
                    value={auditData.escalationDay}
                    name="escalationDay"
                    onChange={(value: number) =>
                      setAuditData((prev) => ({
                        ...prev,
                        escalationDay: value,
                      }))
                    }
                    onBlur={handleRemainderCall}
                    text="Days"
                  />
                </div>
              </div>
            </div>
            <div className="form-group step4background">
              <KickOff className="Frequencyalign" />
              <strong>Kickoff Meeting</strong>
              <div className="toggle-wrapper">
                <div className="toggle-label ">
                  <p className="mb-3">Required?</p>
                </div>

                <div className="toggle-control inside">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={handleToggle}
                    />
                    <span className="slider">
                      <span className="yes-text">Yes</span>
                      <span className="no-text">No</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <AuditSequencing
            policies={transformedSequences}
            onSequenceUpdate={handleSequenceUpdate}
            initialSteps={reconstructInitialSteps()}
          />
        </div>
      </div>
    </div>
  );
};

export default Step4AuditPreference;
